import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Directive, EventEmitter, HostBinding, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { cachedAsObservable } from './lib';
import { ValueAccessor } from './value-accessor';
import { WOption } from './w-option';
import { isNotEmptyOrHiddenElement } from './change-observer';


let inputId = 0;
const ids: Record<string, number> = {}; // used to generate unique ids for inputs
export function getInputId(name: string): string {
    if (!ids[name]) {
        ids[name] = 0;
    }
    ids[name]++;
    return `${name}-${ids[name]}`;
}
@Directive({
    host: {
        '[class.b-input--is-required]': 'required && label', // show * only if there is a label
        '[class.b-input--is-edit-denied]': 'isDisabled',
        '[class.b-input--is-invalid]': 'invalid',
        '[attr.title]': 'tooltip',
        '[attr.required]': 'required', // can be still required field for validation purposes
    },
    standalone: false
})
/*
    parent class for w-input-xxx components
    provides ngModule syntax and some standard fields/events
    NOTE: parent class that contains inputs/outputs should be in lib/internal directory (for doc script)
*/
// tslint:disable-next-line: directive-class-suffix
export abstract class WInputBase extends ValueAccessor implements OnInit {

    @Input() id!: string; // used for form control, if not provided, generated automatically
    @Input() name!: string; // used for form control, if not provided, generated automatically

    @Input() label: string = '';
    @Input() placeholder: string = ''; // shown if no label or value
    @Input() info: string = ''; // or use facet-info
    @Input() error: string = ''; // or use facet-error
    @Input() title: string = '';  // tooltip
    @Input() invalid: boolean = false;
    @Output() wFocus = new EventEmitter<FocusEvent>(); // emits event causing focus, can be FocusEvent, can be other Event
    @Output() wBlur = new EventEmitter<FocusEvent>(); // emits on blur, can be FocusEvent, can be other Event

    @Input() set disabled(value: boolean) {
        this.setDisabledState(value);
    }
    get disabled() {
        return this.getProperty('disabled');
    }
    @Input() required: boolean = false;

    get tooltip(): string {
        return this.attr(this.title);
    }
    get isDisabled(): boolean {
        return this.disabled;
    }

    empty = new BehaviorSubject(true);
    watchables = new Map<HTMLElement, Subject<boolean>>();

    isInputActive: boolean = false;

    ngOnInit(): void {
        this.id = this.id || getInputId('input'); // generate if not provided
        this.name = this.name || this.id; // use id as name if not provided
    }
    attr(x: any) {
        return x ? x : undefined;
    }
    optionHasProp(option: WOption | string, name: string): boolean {
        return typeof option !== 'string' && !!(option as Record<string, string>)[name];
    }
    optionPropIf(option: WOption | string, name: string): string | undefined {
        return (typeof option !== 'string' && !!(option as Record<string, string>)[name]) ?
            (option as Record<string, string>)[name] : undefined;
    }

    @HostBinding('class.b-input--is-focused')
    get ifoc(): boolean {
        return this.isInputActive;
    }
    @HostListener('focusin', ['$event']) whenFocus(e: FocusEvent) {
        setTimeout(() => {
            this.isInputActive = true;
        });
    }
    @HostListener('focusout', ['$event']) whenOut(e: FocusEvent) {
        setTimeout(() => {
            this.isInputActive = false;
        });
    }
    getLabel(option: string | WOption): string {
        return typeof option === 'string' ? option : (option.label || '');
    }
    getLabelAsync(option: string | WOption): Observable<any> {
        const o = this.getLabel(option) as string;
        if (typeof option !== 'string' && option.labelAsync) {
            return option.labelAsync;
        } else {
            return cachedAsObservable(o);
        }
    }
    getValue(option: string | WOption): string {
        return (typeof option === 'string') ? option : option.value;
    }
    // watch HTML elements for their content
    isEmptyElement(el: HTMLElement): Subject<boolean> | undefined {
        if (!this.watchables.has(el)) {
            this.watchables.set(el, new Subject<boolean>());
            this.runOnMutation(el, { childList: true }, this.checkEmpty.bind(this, el));
            setTimeout(() => {
                this.checkEmpty(el);
            });
        }
        return this.watchables.get(el);
    }
    checkEmpty(el: HTMLElement) {
        const isNotEmpty = isNotEmptyOrHiddenElement(el);
        this.watchables.get(el)?.next(!isNotEmpty);
    }
}
